import { Button, Dropdown, MenuProps } from 'antd';
import React, { useMemo } from 'react';
import { Link, Icon } from '@common/components';

import { MissionControlStaffingRequestApplicationType } from '@applicant-flow/graphql';
import { buildDialogUrl, createDropdownMenuItem } from '@common/services';

type ApplicationActionsType = Pick<
  MissionControlStaffingRequestApplicationType,
  'id' | 'activeOffer' | 'state' | 'staffingRequest' | 'worksSubmittalPacket'
>;

const generateMenuItems = ({
  application,
  shortlistForReview,
  retractOffer,
}: {
  application: ApplicationActionsType;
  shortlistForReview?: () => void;
  reject?: () => void;
  retractOffer?: () => void;
}): MenuProps['items'] => {
  const { id, activeOffer, state, staffingRequest, worksSubmittalPacket } =
    application;
  const submittedStates = ['submitted', 'submittal_packet_change_requested'];
  const offeredStates = ['offered', 'change_requested'];

  const rejectApplicationDrawerUrl = buildDialogUrl(
    'application-rejection-drawer',
    {
      props: { applicationId: id },
    }
  );

  const viewOfferDrawerUrl = activeOffer
    ? buildDialogUrl('offer-preview-drawer', {
        props: { offerId: activeOffer.id },
      })
    : '';

  const submittalPacketPreviewerDrawerDrawerUrl = worksSubmittalPacket
    ? buildDialogUrl('submittal-packet-previewer-drawer', {
        props: {
          applicationId: id,
          applicationState: state,
          submittalPacketId: worksSubmittalPacket.id,
        },
      })
    : '';

  return [
    {
      key: '1',
      style: { width: '180px' },
      label: (
        <Link to={`/works/applications/${id}/summary`} target="_blank">
          View Application{' '}
          <Icon
            className="wk-external-link__icon"
            icon="arrowUpRightFromSquare"
            size="sm"
          />
        </Link>
      ),
    },
    ...(createDropdownMenuItem({
      key: '2',
      label: <Link to={viewOfferDrawerUrl}>View Offer</Link>,
      condition: !!activeOffer?.id,
    }) ?? []),
    ...(createDropdownMenuItem({
      key: '3',
      label: (
        <Link to={submittalPacketPreviewerDrawerDrawerUrl}>
          Submittal Packet
        </Link>
      ),
      condition: !!worksSubmittalPacket?.id,
    }) ?? []),
    ...(createDropdownMenuItem({
      key: '4',
      label: <a onClick={shortlistForReview}>Shortlist for Review</a>,
      condition:
        submittedStates.includes(state) && staffingRequest.state === 'OPEN',
    }) ?? []),
    ...(createDropdownMenuItem({
      key: '5',
      label: <a onClick={retractOffer}>Retract Offer</a>,
      condition:
        state === 'offer' &&
        !!activeOffer &&
        offeredStates.includes(activeOffer.state) &&
        activeOffer.canRetract?.value,
    }) ?? []),
    ...(createDropdownMenuItem({
      key: '6',
      label: <Link to={rejectApplicationDrawerUrl}>Reject Application</Link>,
      condition: !activeOffer?.id,
    }) ?? []),
  ];
};

export const ApplicationActionsDropdown = ({
  application,
  shortlistForReview,
  retractOffer,
}: {
  application: ApplicationActionsType;
  refetchQueries?: string[];
  shortlistForReview: (applicationId?: string | null) => void;
  retractOffer: (offerId?: string | null) => void;
}) => {
  const menuItems = useMemo(
    () =>
      application
        ? generateMenuItems({
            application,
            shortlistForReview: () => shortlistForReview(application.id),
            retractOffer: () => retractOffer(application.activeOffer?.id),
          })
        : [],
    [application, shortlistForReview, retractOffer]
  );

  return (
    <Dropdown
      className="compliance-actions-dropdown"
      menu={{ items: menuItems }}
      trigger={['click']}
    >
      <Button
        type="link"
        shape="circle"
        color="elm"
        style={{ padding: 5 }}
        icon={
          <Icon size="lg" icon="verticalEllipsis" kind="regular" color="elm" />
        }
      />
    </Dropdown>
  );
};
